import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  changeLanguage,
  getCurrentLanguage,
} from '../../lib/i18n';

interface I18nContextType {
  currentLanguage: SupportedLanguage;
  availableLanguages: typeof SUPPORTED_LANGUAGES;
  changeLanguage: (language: SupportedLanguage) => Promise<void>;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] =
    useState<SupportedLanguage>(getCurrentLanguage());

  useEffect(() => {
    const initializeI18n = async () => {
      try {
        // Wait for i18n to be ready
        await i18n.loadLanguages(Object.keys(SUPPORTED_LANGUAGES));

        // Set initial language
        const initialLanguage = getCurrentLanguage();
        setCurrentLanguage(initialLanguage);

        // Update document language attribute
        if (typeof document !== 'undefined') {
          document.documentElement.lang = initialLanguage;
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Failed to initialize i18n:', error);
        setIsLoading(false);
      }
    };

    initializeI18n();
  }, [i18n]);

  const handleLanguageChange = async (language: SupportedLanguage) => {
    try {
      setIsLoading(true);
      await changeLanguage(language);
      setCurrentLanguage(language);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: I18nContextType = {
    currentLanguage,
    availableLanguages: SUPPORTED_LANGUAGES,
    changeLanguage: handleLanguageChange,
    isLoading,
  };

  return (
    <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// Hook for easy translation with fallback
export const useT = () => {
  const { t, i18n } = useTranslation();

  return (key: string, options?: any) => {
    const translation = t(key, options);

    // If translation is missing and we're not in English, show key with warning
    if (translation === key && i18n.language !== 'en') {
      console.warn(
        `Missing translation for key: ${key} in language: ${i18n.language}`
      );
    }

    return translation;
  };
};
